import { ApolloLink, FetchResult, NextLink, Observable, Operation } from '@apollo/client/core';
import { getMainDefinition } from '@apollo/client/utilities';

import {
  HASURA_SCHEMA_SYNC_RETRY_COUNT,
  HASURA_SCHEMA_SYNC_RETRY_DELAY_MS,
  isAuthenticatedHasuraSchemaSyncError
} from './transient-hasura-errors';

/**
 * Global Apollo retry for Hasura replica schema-sync races (#13426 / #11360).
 *
 * Sits in front of every GraphQL operation (query, mutation, subscription) so
 * callers do not each have to retry. When a replica's in-memory schema is
 * briefly missing a root field, we wait and re-issue the operation. After
 * retries are exhausted, subscription errors are completed rather than
 * thrown so Angular async pipes / list managers don't crash the page.
 */
export class HasuraSchemaSyncLink extends ApolloLink {
  constructor(
    private readonly retryCount = HASURA_SCHEMA_SYNC_RETRY_COUNT,
    private readonly retryDelayMs = HASURA_SCHEMA_SYNC_RETRY_DELAY_MS
  ) {
    super();
  }

  request(operation: Operation, forward?: NextLink): Observable<FetchResult> {
    if (!forward) {
      throw new Error('HasuraSchemaSyncLink requires a NextLink');
    }
    return new Observable<FetchResult>(observer => {
      let innerSub: { unsubscribe(): void } | undefined;
      let timer: ReturnType<typeof setTimeout> | undefined;
      let cancelled = false;
      let retrying = false;

      const run = (attempt: number) => {
        if (cancelled) {
          return;
        }
        retrying = false;
        innerSub = forward(operation).subscribe({
          next: (result: FetchResult) => {
            if (
              result.errors?.length
              && isAuthenticatedHasuraSchemaSyncError(
                { errors: result.errors },
                operation.getContext()
              )
              && attempt < this.retryCount
            ) {
              scheduleRetry(attempt, operation.operationName, 'GraphQL errors');
              return;
            }
            observer.next(result);
          },
          error: (err: unknown) => {
            const isAuthenticatedSchemaSyncError = isAuthenticatedHasuraSchemaSyncError(
              err,
              operation.getContext()
            );
            if (isAuthenticatedSchemaSyncError && attempt < this.retryCount) {
              scheduleRetry(attempt, operation.operationName, 'network/subscription error');
              return;
            }
            if (isAuthenticatedSchemaSyncError && isSubscriptionOperation(operation)) {
              console.warn(
                `[Hasura schema-sync] giving up on subscription ${operation.operationName}; not failing the observable`
              );
              observer.complete();
              return;
            }
            observer.error(err);
          },
          complete: () => {
            if (retrying || cancelled) {
              return;
            }
            observer.complete();
          }
        });
      };

      const scheduleRetry = (attempt: number, operationName: string, reason: string) => {
        retrying = true;
        innerSub?.unsubscribe();
        innerSub = undefined;
        console.warn(
          `[Hasura schema-sync] retrying ${operationName} (${attempt + 1}/${this.retryCount}) after ${reason}`
        );
        timer = setTimeout(() => run(attempt + 1), this.retryDelayMs);
      };

      run(0);

      return () => {
        cancelled = true;
        if (timer) {
          clearTimeout(timer);
        }
        innerSub?.unsubscribe();
      };
    });
  }
}

function isSubscriptionOperation(operation: Operation): boolean {
  const definition = getMainDefinition(operation.query);
  return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
}
